import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { FileText, CheckCircle, ArrowRight, Shield, Zap, Clock, TrendingDown, Wallet, FileCheck, Cloud, Plug, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Section, Container } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LocalizedLink } from '@/components/LocalizedLink';

export function ProductFirmo() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const features = [
    {
      icon: Shield,
      title: t('products.firmo.feature1.title'),
      description: t('products.firmo.feature1.description'),
    },
    {
      icon: Zap,
      title: t('products.firmo.feature2.title'),
      description: t('products.firmo.feature2.description'),
    },
    {
      icon: Clock,
      title: t('products.firmo.feature3.title'),
      description: t('products.firmo.feature3.description'),
    },
  ];

  const benefits = t('products.firmo.benefits', { returnObjects: true }) as string[];

  const valueProps = [
    {
      icon: TrendingDown,
      stat: '80%',
      label: t('products.firmo.stats.time'),
      description: t('products.firmo.stats.timeDesc'),
    },
    {
      icon: Wallet,
      stat: '60%',
      label: t('products.firmo.stats.cost'),
      description: t('products.firmo.stats.costDesc'),
    },
    {
      icon: FileCheck,
      stat: '100%',
      label: t('products.firmo.stats.legal'),
      description: t('products.firmo.stats.legalDesc'),
    },
  ];

  const detailedBenefits = [
    {
      icon: Zap,
      title: t('products.firmo.detailedBenefits.speed.title'),
      description: t('products.firmo.detailedBenefits.speed.description'),
    },
    {
      icon: Shield,
      title: t('products.firmo.detailedBenefits.security.title'),
      description: t('products.firmo.detailedBenefits.security.description'),
    },
    {
      icon: Cloud,
      title: t('products.firmo.detailedBenefits.cloud.title'),
      description: t('products.firmo.detailedBenefits.cloud.description'),
    },
    {
      icon: Plug,
      title: t('products.firmo.detailedBenefits.integration.title'),
      description: t('products.firmo.detailedBenefits.integration.description'),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('products.firmo.pageTitle')}</title>
        <meta name="description" content={t('products.firmo.metaDescription')} />
        <link rel="canonical" href={`https://www.01sistemi.it/${currentLang}/firmo-semplice`} />
      </Helmet>

      {/* Hero */}
      <Section className="bg-gradient-to-br from-cta-50 to-white" padding="lg">
        <Container>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-5">
              <div className="flex items-center gap-2">
                <span className="inline-block px-3 py-1.5 bg-cta-100 text-cta-600 rounded-full text-sm font-medium">
                  {t('products.firmo.badge')}
                </span>
                <span className="text-sm text-gray-500">{t('products.firmo.by')}</span>
              </div>
              <h1 className="heading-1">
                Firmo<span className="text-cta-500">SEMPLICE</span>
              </h1>
              <p className="body-large">
                {t('products.firmo.heroDescription')}
              </p>
              <div className="flex flex-wrap gap-3">
                <LocalizedLink to="contacts">
                  <Button variant="cta" size="lg">
                    {t('products.firmo.ctaButton')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </LocalizedLink>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-gradient-to-br from-cta-400 to-cta-600 rounded-2xl shadow-xl flex items-center justify-center">
                <div className="text-center text-white p-8">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-90" />
                  <div className="text-2xl font-bold">FirmoSEMPLICE</div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Features */}
      <Section padding="lg">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="caption text-cta-500">{t('products.firmo.featuresTitle')}</span>
            <h2 className="heading-2 mt-3">{t('products.firmo.featuresSubtitle')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature) => (
              <Card key={feature.title} variant="hover">
                <div className="w-10 h-10 bg-cta-100 rounded-lg flex items-center justify-center mb-3">
                  <feature.icon className="w-5 h-5 text-cta-500" />
                </div>
                <h3 className="heading-4 mb-2">{feature.title}</h3>
                <p className="body-small">{feature.description}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      {/* Benefits - Transform Your Document Management */}
      <Section className="bg-gradient-to-b from-white via-gray-50 to-white py-20 lg:py-28">
        <Container>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-cta-100 text-cta-700 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t('products.firmo.benefitsOverline')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              {t('products.firmo.benefitsTitlePart1')}
              <span className="text-cta-500"> {t('products.firmo.benefitsTitleHighlight')} </span>
              {t('products.firmo.benefitsTitlePart2')}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('products.firmo.benefitsDescription')}
            </p>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid md:grid-cols-3 gap-6 mb-20"
          >
            {valueProps.map((prop, index) => (
              <motion.div
                key={prop.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="relative group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg shadow-gray-200/50 border border-gray-100 text-center hover:shadow-xl hover:shadow-cta-500/10 hover:border-cta-200 transition-all duration-300 h-full flex flex-col">
                  <div className="w-16 h-16 bg-gradient-to-br from-cta-400 to-cta-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <prop.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-5xl font-bold text-cta-500 mb-2">{prop.stat}</div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">{prop.label}</div>
                  <p className="text-gray-600 flex-1">{prop.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-20">
            {/* Left Column - Story */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                {t('products.firmo.storyTitle')}
              </h3>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>{t('products.firmo.storyParagraph1')}</p>
                <p>{t('products.firmo.storyParagraph2')}</p>
                <p>{t('products.firmo.storyParagraph3')}</p>
              </div>

              {/* Quick Benefits List */}
              <div className="pt-4 space-y-3">
                {benefits.slice(0, 4).map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 * index }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-cta-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-cta-600" />
                    </div>
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Column - Feature Cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid sm:grid-cols-2 gap-4"
            >
              {detailedBenefits.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-cta-200 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-cta-100 to-cta-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-6 h-6 text-cta-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Container>
      </Section>
    </>
  );
}
